/**
 * DISC color constants, labels, mappings and mock data.
 * Used by DISCResultsPanel and related chart components.
 */

// ── DISC Colors ──────────────────────────────────────────────────────
export const DISC_COLORS = {
  D: '#CB3333',
  I: '#DCC23A',
  S: '#1EB050',
  C: '#2B6CB0',
};

// ── DISC → ARTS display mapping ─────────────────────────────────────
export const DISC_TO_ARTS = { D: 'A', I: 'S', S: 'T', C: 'R' };

// ── DISC Labels (ARTS names) ────────────────────────────────────────
export const DISC_LABELS = {
  D: 'Asertividad',
  I: 'Sociabilidad',
  S: 'Tolerancia',
  C: 'Reglas',
};

// ── Ordered dimensions ───────────────────────────────────────────────
export const DISC_DIMENSIONS = ['D', 'I', 'S', 'C'];

// ── ARTS display order (A, S, T, R) mapped to DISC keys ─────────────
export const ARTS_ORDER = ['D', 'I', 'S', 'C'];

// ── Competencia → Factor DISC mapping ────────────────────────────────
export const COMPETENCIA_DISC_MAP = {
  // I – Influencia (Amarillo)
  'Comunicación': 'I',
  'Influencia y Persuasión': 'I',
  'Innovación': 'I',
  'Flexibilidad': 'S',
  'Agilidad en la Satisfacción al Cliente': 'I',
  'Liderazgo en red - Competencia digital': 'I',
  'Comunicación digital': 'I',
  // D – Dominancia (Rojo)
  'Iniciativa': 'D',
  'Negociación': 'D',
  'Liderazgo': 'D',
  'Enfoque a Resultados': 'D',
  'Autoconfianza': 'D',
  'Visión Integral': 'D',
  'Toma de decisiones y Resolución de problemas': 'D',
  'Enfoque Estratégico': 'D',
  'Orientación al Mercado': 'D',
  'Autogestión del aprendizaje - Competencia digital': 'D',
  'Dirección de personas': 'D',
  // S – Estabilidad (Verde)
  'Trabajo en equipo': 'S',
  'Adaptación Ágil al Cambio': 'S',
  'Colaboración en red - Competencia digital': 'S',
  'Gestión del compromiso': 'S',
  'Perseverancia': 'S',
  'Sensibilidad Interpersonal': 'S',
  'Sensibilidad Política': 'S',
  'Agente de Clima': 'S',
  'Trabajo colaborativo': 'S',
  'Desarrollo de Personas': 'S',
  // C – Cumplimiento (Azul)
  'Búsqueda de Información': 'C',
  'Enfoque al Cliente': 'C',
  'Planificación': 'C',
  'Gestión del Riesgo': 'C',
  'Habilidades Operativas': 'C',
  'Orientación a la Calidad': 'C',
  'Gestión Digital de la Información': 'C',
};

// ── Helpers ──────────────────────────────────────────────────────────

export function getCompetenciaDiscFactor(comp) {
  return COMPETENCIA_DISC_MAP[comp];
}

export function hexToRgba(hex, opacity) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const DISC_WHEEL_MIN_OPACITY = 0.35;
const DISC_WHEEL_OPACITY_RANGE = 0.65;

export function clampDiscPercentage(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.min(100, numericValue));
}

export function getDiscWheelOpacity(value) {
  const normalized = clampDiscPercentage(value) / 100;
  return DISC_WHEEL_MIN_OPACITY + normalized * DISC_WHEEL_OPACITY_RANGE;
}

export function getDiscWheelLabelColor() {
  return '#FFFFFF';
}

export function getDiscWheelLabelStrokeColor() {
  return '#000000';
}

export function getDominantFactor(profile) {
  let max = 0;
  let dominant = 'D';
  for (const [k, v] of Object.entries(profile)) {
    if (v > max) {
      max = v;
      dominant = k;
    }
  }
  return dominant;
}

// ── Mock DISC Data ───────────────────────────────────────────────────
// TODO: Replace with real API data

export const DISC_DATA = {
  '1': { name: 'Jorge Gomez', role: 'Desarrollador', campaign: 'Campaña Q4 2024', natural: { D: 75, I: 45, S: 30, C: 65 }, adapted: { D: 85, I: 55, S: 25, C: 70 } },
  '2': { name: 'Camilo Perez', role: 'Proactivo', campaign: 'Campaña Q4 2024', natural: { D: 40, I: 80, S: 60, C: 35 }, adapted: { D: 45, I: 85, S: 55, C: 40 } },
  '3': { name: 'Marta Lorena', role: 'Jefa de Fiscalizador', campaign: 'Campaña Q4 2024', natural: { D: 90, I: 35, S: 25, C: 75 }, adapted: { D: 95, I: 40, S: 20, C: 80 } },
  '4': { name: 'Juan Gimenez', role: 'Desarrollador', campaign: 'Campaña Q4 2024', natural: { D: 30, I: 50, S: 80, C: 60 }, adapted: { D: 35, I: 55, S: 75, C: 65 } },
  '5': { name: 'Carlos Lopez', role: 'Jefe de Fiscalizador', campaign: 'Campaña Q4 2024', natural: { D: 65, I: 70, S: 45, C: 50 }, adapted: { D: 70, I: 75, S: 40, C: 55 } },
};

// fallback for dev — real data comes from /api/results/reports/disc/gap
export const COMPETENCIA_IDEAL = {
  'Comunicación': 80, 'Influencia y Persuasión': 78, 'Iniciativa': 75, 'Búsqueda de Información': 70,
  'Negociación': 76, 'Innovación': 72, 'Liderazgo': 85, 'Enfoque a Resultados': 82,
  'Enfoque al Cliente': 78, 'Planificación': 74, 'Trabajo en equipo': 76, 'Flexibilidad': 68,
  'Autoconfianza': 72, 'Adoptación Ágil al Cambio': 70, 'Agilidad en la Satisfacción al Cliente': 75,
  'Visión Integral': 68, 'Toma de decisiones y Resolución de problemas': 80, 'Gestión del compromiso': 72,
  'Desarrollo de Personas': 78, 'Enfoque Estratégico': 76, 'Perseverancia': 74, 'Gestión del Riesgo': 70,
  'Liderazgo en red - Competencia digital': 65, 'Autogestión del aprendizaje - Competencia digital': 68,
  'Sensibilidad Interpersonal': 72, 'Orientación al Mercado': 70, 'Habilidades Operativas': 74,
  'Sensibilidad Política': 66, 'Agente de Clima': 70, 'Orientación a la Calidad': 76,
  'Trabajo colaborativo': 78, 'Comunicación digital': 68, 'Gestión Digital de la Información': 65,
  'Dirección de personas': 80,
};

// fallback for dev — real data comes from /api/results/reports/disc/gap
export const COMPETENCIA_ACTUAL = {
  '1': { 'Comunicación': 60, 'Influencia y Persuasión': 70, 'Iniciativa': 78, 'Búsqueda de Información': 65, 'Negociación': 72, 'Innovación': 68, 'Liderazgo': 82, 'Enfoque a Resultados': 88, 'Enfoque al Cliente': 58, 'Planificación': 75, 'Trabajo en equipo': 55, 'Flexibilidad': 50, 'Autoconfianza': 78, 'Adoptación Ágil al Cambio': 62, 'Agilidad en la Satisfacción al Cliente': 65, 'Visión Integral': 70, 'Toma de decisiones y Resolución de problemas': 78, 'Gestión del compromiso': 48, 'Desarrollo de Personas': 72, 'Enfoque Estratégico': 75, 'Perseverancia': 80, 'Gestión del Riesgo': 72, 'Liderazgo en red - Competencia digital': 55, 'Autogestión del aprendizaje - Competencia digital': 60, 'Sensibilidad Interpersonal': 52, 'Orientación al Mercado': 65, 'Habilidades Operativas': 78, 'Sensibilidad Política': 58, 'Agente de Clima': 55, 'Orientación a la Calidad': 78, 'Trabajo colaborativo': 60, 'Comunicación digital': 62, 'Gestión Digital de la Información': 58, 'Dirección de personas': 75 },
  '2': { 'Comunicación': 85, 'Influencia y Persuasión': 88, 'Iniciativa': 55, 'Búsqueda de Información': 45, 'Negociación': 80, 'Innovación': 58, 'Liderazgo': 55, 'Enfoque a Resultados': 60, 'Enfoque al Cliente': 82, 'Planificación': 45, 'Trabajo en equipo': 72, 'Flexibilidad': 68, 'Autoconfianza': 55, 'Adoptación Ágil al Cambio': 72, 'Agilidad en la Satisfacción al Cliente': 78, 'Visión Integral': 50, 'Toma de decisiones y Resolución de problemas': 45, 'Gestión del compromiso': 70, 'Desarrollo de Personas': 62, 'Enfoque Estratégico': 48, 'Perseverancia': 58, 'Gestión del Riesgo': 40, 'Liderazgo en red - Competencia digital': 52, 'Autogestión del aprendizaje - Competencia digital': 55, 'Sensibilidad Interpersonal': 78, 'Orientación al Mercado': 72, 'Habilidades Operativas': 50, 'Sensibilidad Política': 68, 'Agente de Clima': 75, 'Orientación a la Calidad': 42, 'Trabajo colaborativo': 80, 'Comunicación digital': 72, 'Gestión Digital de la Información': 65, 'Dirección de personas': 52 },
  '3': { 'Comunicación': 50, 'Influencia y Persuasión': 55, 'Iniciativa': 92, 'Búsqueda de Información': 85, 'Negociación': 48, 'Innovación': 70, 'Liderazgo': 92, 'Enfoque a Resultados': 95, 'Enfoque al Cliente': 55, 'Planificación': 80, 'Trabajo en equipo': 45, 'Flexibilidad': 40, 'Autoconfianza': 90, 'Adoptación Ágil al Cambio': 55, 'Agilidad en la Satisfacción al Cliente': 50, 'Visión Integral': 82, 'Toma de decisiones y Resolución de problemas': 88, 'Gestión del compromiso': 42, 'Desarrollo de Personas': 85, 'Enfoque Estratégico': 88, 'Perseverancia': 90, 'Gestión del Riesgo': 85, 'Liderazgo en red - Competencia digital': 48, 'Autogestión del aprendizaje - Competencia digital': 52, 'Sensibilidad Interpersonal': 40, 'Orientación al Mercado': 58, 'Habilidades Operativas': 82, 'Sensibilidad Política': 62, 'Agente de Clima': 38, 'Orientación a la Calidad': 82, 'Trabajo colaborativo': 48, 'Comunicación digital': 50, 'Gestión Digital de la Información': 55, 'Dirección de personas': 90 },
  '4': { 'Comunicación': 65, 'Influencia y Persuasión': 58, 'Iniciativa': 82, 'Búsqueda de Información': 90, 'Negociación': 55, 'Innovación': 95, 'Liderazgo': 48, 'Enfoque a Resultados': 50, 'Enfoque al Cliente': 72, 'Planificación': 68, 'Trabajo en equipo': 85, 'Flexibilidad': 78, 'Autoconfianza': 62, 'Adoptación Ágil al Cambio': 80, 'Agilidad en la Satisfacción al Cliente': 72, 'Visión Integral': 75, 'Toma de decisiones y Resolución de problemas': 55, 'Gestión del compromiso': 82, 'Desarrollo de Personas': 55, 'Enfoque Estratégico': 50, 'Perseverancia': 65, 'Gestión del Riesgo': 60, 'Liderazgo en red - Competencia digital': 70, 'Autogestión del aprendizaje - Competencia digital': 75, 'Sensibilidad Interpersonal': 80, 'Orientación al Mercado': 62, 'Habilidades Operativas': 65, 'Sensibilidad Política': 55, 'Agente de Clima': 78, 'Orientación a la Calidad': 65, 'Trabajo colaborativo': 85, 'Comunicación digital': 78, 'Gestión Digital de la Información': 72, 'Dirección de personas': 45 },
  '5': { 'Comunicación': 78, 'Influencia y Persuasión': 75, 'Iniciativa': 78, 'Búsqueda de Información': 75, 'Negociación': 68, 'Innovación': 72, 'Liderazgo': 78, 'Enfoque a Resultados': 72, 'Enfoque al Cliente': 68, 'Planificación': 58, 'Trabajo en equipo': 62, 'Flexibilidad': 58, 'Autoconfianza': 75, 'Adoptación Ágil al Cambio': 65, 'Agilidad en la Satisfacción al Cliente': 68, 'Visión Integral': 62, 'Toma de decisiones y Resolución de problemas': 65, 'Gestión del compromiso': 55, 'Desarrollo de Personas': 68, 'Enfoque Estratégico': 65, 'Perseverancia': 70, 'Gestión del Riesgo': 52, 'Liderazgo en red - Competencia digital': 60, 'Autogestión del aprendizaje - Competencia digital': 62, 'Sensibilidad Interpersonal': 65, 'Orientación al Mercado': 70, 'Habilidades Operativas': 72, 'Sensibilidad Política': 60, 'Agente de Clima': 62, 'Orientación a la Calidad': 55, 'Trabajo colaborativo': 65, 'Comunicación digital': 60, 'Gestión Digital de la Información': 58, 'Dirección de personas': 72 },
};

// ── NW-2504: Heatmap helpers ─────────────────────────────────────────

/**
 * Returns color bucket, solid hex, and SVG pattern ID for a given
 * absolute score (0-100). Uses brute scale — NOT ratio-based.
 *
 * Buckets (spec §ADD-3):
 *   0-39   → red
 *   40-57  → yellow
 *   58-100 → green
 *   null/NaN → na
 */
export function getHeatmapColorByScore(score) {
  if (score == null || Number.isNaN(score)) {
    return { bucket: 'na', color: '#E5E7EB', patternId: null };
  }
  if (score <= 39) {
    return { bucket: 'red', color: DISC_COLORS.D, patternId: 'nw2504-heatmap-stripes-red' };
  }
  if (score <= 57) {
    return { bucket: 'yellow', color: DISC_COLORS.I, patternId: 'nw2504-heatmap-stripes-yellow' };
  }
  return { bucket: 'green', color: DISC_COLORS.S, patternId: 'nw2504-heatmap-stripes-green' };
}

/**
 * Aggregates discGap.persons into a HeatmapRow[] for each selected competency.
 * Persons with is_inconclusive===true OR profile==='Inconsistente' are excluded.
 *
 * @param {object|null} discGap — Redux state: { persons: DiscGapPerson[] }
 * @param {Array<{id:number, title:string}>} selectedCompetencias
 * @returns {Array<HeatmapRow>}
 *
 * HeatmapRow: { fullName, avg, ideal, count, status }
 */
export function computeGroupHeatmapData(discGap, selectedCompetencias) {
  if (!discGap?.persons?.length || !selectedCompetencias?.length) return [];

  const validPersons = discGap.persons.filter(
    (p) => !p.is_inconclusive && p.profile !== 'Inconsistente'
  );

  return selectedCompetencias.map((comp) => {
    let sum = 0;
    let count = 0;
    let ideal = 0;

    for (const person of validPersons) {
      const row = person.competencias?.find(
        (c) =>
          c.name_es === comp.title ||
          c.name_en === comp.title ||
          c.name_pt === comp.title
      );
      if (row && typeof row.actual === 'number') {
        sum += row.actual;
        count += 1;
        ideal = row.ideal;
      }
    }

    if (count === 0) {
      return { fullName: comp.title, name_es: comp.name_es, avg: null, ideal: 0, count: 0, status: 'na' };
    }

    return {
      fullName: comp.title,
      name_es: comp.name_es,
      avg: Math.max(0, Math.round(sum / count)),
      ideal,
      count,
      status: 'ok',
    };
  });
}

// ── Data helpers ─────────────────────────────────────────────────────

export function getDiscValues(personId, viewType) {
  const data = DISC_DATA[personId];
  if (!data) return { D: 0, I: 0, S: 0, C: 0 };
  return viewType === 'natural' ? data.natural : data.adapted;
}

export function averageDiscValues(personIds, viewType) {
  const vals = personIds.map((id) => getDiscValues(id, viewType));
  const avg = { D: 0, I: 0, S: 0, C: 0 };
  vals.forEach((v) => { avg.D += v.D; avg.I += v.I; avg.S += v.S; avg.C += v.C; });
  const n = vals.length || 1;
  return {
    D: Math.round(avg.D / n),
    I: Math.round(avg.I / n),
    S: Math.round(avg.S / n),
    C: Math.round(avg.C / n),
  };
}

/**
 * Largest Remainder Method — distributes 100 percentage points across items
 * guaranteeing the sum is exactly 100.
 *
 * @param {Array<{ key: string, count: number }>} items
 * @param {number} total — sum of all counts
 * @returns {Array<{ key: string, pct: number }>}
 */
export function largestRemainderRound(items, total) {
  if (total <= 0) return items.map(({ key }) => ({ key, pct: 0 }));

  const withRaw = items.map(({ key, count }) => {
    const raw = (count / total) * 100;
    const floor = Math.floor(raw);
    return { key, count, raw, floor, remainder: raw - floor, pct: undefined };
  });

  const sumFloors = withRaw.reduce((s, p) => s + p.floor, 0);
  let deficit = 100 - sumFloors;

  // Sort by remainder descending to assign +1 to largest remainders first,
  // but only to items that actually have count > 0.
  const sorted = [...withRaw].sort((a, b) => {
    if (a.count === 0 && b.count > 0) return 1;
    if (b.count === 0 && a.count > 0) return -1;
    return b.remainder - a.remainder;
  });

  for (let i = 0; i < sorted.length && deficit > 0; i++) {
    if (sorted[i].count > 0) {
      sorted[i].pct = sorted[i].floor + 1;
      deficit--;
    }
  }

  withRaw.forEach((p) => { if (p.pct === undefined) p.pct = p.floor; });

  return withRaw.map(({ key, pct }) => ({ key, pct }));
}

export function computeGapData(personIds, selectedCompetencias) {
  return selectedCompetencias.map((comp) => {
    const ideal = COMPETENCIA_IDEAL[comp] ?? 70;
    let actual = 0;
    let count = 0;
    personIds.forEach((id) => {
      const val = COMPETENCIA_ACTUAL[id]?.[comp];
      if (val !== undefined) { actual += val; count++; }
    });
    const avgActual = count > 0 ? Math.round(actual / count) : 0;
    const gap = avgActual - ideal;
    return {
      name: comp.length > 18 ? comp.substring(0, 18) + '…' : comp,
      fullName: comp,
      actual: avgActual,
      ideal,
      gap,
    };
  });
}
